import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { format } from "date-fns";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { X } from "lucide-react";
import { ScrollArea } from "./ui/scroll-area";
import { ActivityLog } from "../lib/api";
import { getBadgeVariant } from "../lib/utils";
import SidePanel, { PanelHeader } from "./side-panel";
import { useIsMobile } from "../hooks/use-is-mobile";

interface ActivityLogDetailsDrawerProps {
  log: ActivityLog | null;
  onClose: () => void;
}

function formatDetailsJson(details: Record<string, unknown> | null): string {
  if (!details) return "—";
  try {
    return JSON.stringify(details, null, 2);
  } catch {
    return String(details);
  }
}

interface MobileRow {
  label: string;
  value: React.ReactNode;
  className?: string;
}

const MobileSection: React.FC<{ title: string; rows: MobileRow[] }> = ({
  title,
  rows,
}) => (
  <section className="rounded-2xl border border-gray-100 bg-white">
    <h3 className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
      {title}
    </h3>
    <dl className="divide-y divide-gray-100">
      {rows.map((row) => (
        <div
          key={row.label}
          className="flex items-start justify-between gap-4 px-4 py-3"
        >
          <dt className="shrink-0 text-sm text-gray-500">{row.label}</dt>
          <dd
            className={`min-w-0 break-all text-right text-sm text-gray-900 ${row.className ?? "font-medium"}`}
          >
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  </section>
);

const ActivityLogDetailsDrawer: React.FC<ActivityLogDetailsDrawerProps> = ({
  log,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  const detailsBlock = log && (
    <section className="space-y-2">
      <h3
        className={
          isMobile
            ? "text-xs font-semibold uppercase tracking-wide text-gray-500"
            : "font-semibold text-base"
        }
      >
        Details
      </h3>
      <pre className="text-xs bg-gray-50 border border-gray-100 rounded-lg p-4 overflow-auto max-h-[320px] whitespace-pre-wrap break-words">
        {formatDetailsJson(log.details)}
      </pre>
    </section>
  );

  const drawerContent = (
    <SidePanel open={!!log} onClose={onClose} width="w-[640px]" layer="overlay">
      {log && (
        <div className="relative h-full flex flex-col">
          {isMobile ? (
            <PanelHeader
              title="Activity log details"
              subtitle={format(
                new Date(log.createdAt),
                "MMMM dd, yyyy 'at' hh:mm a",
              )}
              onClose={onClose}
            />
          ) : (
            <div className="px-6 py-4 flex-shrink-0 border-b border-gray-100">
              <div className="flex items-start gap-3">
                <div className="mr-auto min-w-0">
                  <h2 className="text-lg font-semibold text-foreground font-jakarta">
                    Activity log details
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {format(
                      new Date(log.createdAt),
                      "MMMM dd, yyyy 'at' hh:mm a",
                    )}
                  </p>
                </div>
                <Badge
                  variant={getBadgeVariant(log.action)}
                  className="shrink-0"
                >
                  {log.action}
                </Badge>
                <Button
                  variant="outline"
                  className="h-8 w-8 p-0 shrink-0"
                  onClick={onClose}
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
          <div className="flex-grow overflow-hidden">
            <ScrollArea className="h-full">
              {isMobile ? (
                <div className="w-full space-y-4 p-4 pb-8">
                  <MobileSection
                    title="Action"
                    rows={[
                      {
                        label: "Action",
                        value: (
                          <Badge variant={getBadgeVariant(log.action)}>
                            {log.action}
                          </Badge>
                        ),
                      },
                      { label: "Entity type", value: log.entityType },
                      {
                        label: "Entity ID",
                        value: log.entityId ?? "—",
                        className: "font-mono text-xs",
                      },
                    ]}
                  />
                  {log.user && (
                    <MobileSection
                      title="Performed by"
                      rows={[
                        { label: "Name", value: log.user.fullName },
                        { label: "Email", value: log.user.email },
                        { label: "Role", value: log.user.role },
                        {
                          label: "Status",
                          value: (
                            <Badge variant={getBadgeVariant(log.user.status)}>
                              {log.user.status}
                            </Badge>
                          ),
                        },
                      ]}
                    />
                  )}
                  <MobileSection
                    title="Client"
                    rows={[
                      {
                        label: "IP address",
                        value: log.ipAddress ?? "—",
                        className: "font-mono text-xs",
                      },
                      {
                        label: "User agent",
                        value: log.userAgent ?? "—",
                        className: "text-xs",
                      },
                    ]}
                  />
                  {detailsBlock}
                </div>
              ) : (
                <div className="w-full p-6 space-y-6">
                  <section className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <h3 className="font-semibold text-base">Action</h3>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <dt className="text-gray-500">Entity type</dt>
                        <dd className="font-medium mt-0.5">{log.entityType}</dd>
                      </div>
                      <div className="sm:col-span-2">
                        <dt className="text-gray-500">Entity ID</dt>
                        <dd className="font-mono text-xs mt-0.5 break-all">
                          {log.entityId ?? "—"}
                        </dd>
                      </div>
                    </dl>
                  </section>

                  {log.user && (
                    <section className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <h3 className="font-semibold text-base">Performed by</h3>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div>
                          <dt className="text-gray-500">Name</dt>
                          <dd className="font-medium mt-0.5">
                            {log.user.fullName}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Email</dt>
                          <dd className="mt-0.5 break-all">{log.user.email}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Role</dt>
                          <dd className="mt-0.5">{log.user.role}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Status</dt>
                          <dd className="mt-0.5">
                            <Badge variant={getBadgeVariant(log.user.status)}>
                              {log.user.status}
                            </Badge>
                          </dd>
                        </div>
                      </dl>
                    </section>
                  )}

                  <section className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <h3 className="font-semibold text-base">Client</h3>
                    <dl className="grid grid-cols-1 gap-3 text-sm">
                      <div>
                        <dt className="text-gray-500">IP address</dt>
                        <dd className="font-mono text-xs mt-0.5">
                          {log.ipAddress ?? "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">User agent</dt>
                        <dd className="text-xs mt-0.5 break-all text-gray-800">
                          {log.userAgent ?? "—"}
                        </dd>
                      </div>
                    </dl>
                  </section>

                  {detailsBlock}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      )}
    </SidePanel>
  );

  return createPortal(drawerContent, document.body);
};

export default ActivityLogDetailsDrawer;
